// Neo's receipt in the chat (TOM-354, cloud ADR 0302 §5, boards-v3.md
// "Receipts and Undo"): what one request changed, built from the turn's
// records and never from Neo's words. One part per board the request changed,
// in the order the records name them, then one part per boat whose equipment
// answers Neo wrote (TOM-409, cloud ADR 0312 §9), the names, and one part per
// boat whose notebook it changed (TOM-611, cloud ADR 0374): "Neo noted for
// <boat>", shared with everyone on the boat, undone only as a whole request. The owner's quick readings
// left the web in TOM-526, and their records with them. The receipt's Undo undoes each part by compare-and-swap on its own
// row, and says which parts it could not undo. A request that also changed
// the owner's own looks undoes as a whole instead, through the turn's undo
// route, so its two receipts never undo half of it each (TOM-595).
//
// Pure, apart from undoReceiptPart's and undoTurn's one request, which
// announces the chat line an undo wrote (TOM-662, neoUndoNote.ts).

import {
  boatNameTargetKey,
  parseBoatNameTarget,
  type BoatNameTarget,
  type NeoTurnUndoReason,
} from '@neomaris/protocol';
import type { NeoLabelChange } from './neoNames';

import { api } from '../api';
import { withBoardKinds, type NeoBoardChange, type NeoBoardItemSize } from './neoBoard';
import { announceUndoChatNote } from './neoUndoNote';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function stringOf(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function versionOf(v: unknown): number | null {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null;
}

/**
 * An answer about the boat's equipment Neo wrote on the owner's words (cloud
 * ADR 0312 §9, TOM-408): the `data_answers` record. `before` and `after` are
 * the answer, null for none.
 */
export interface NeoDataAnswerChange {
  boatId: string;
  /** `make_model` for the make and model of the boat's own gear (cloud ADR 0425, TOM-878). */
  kind?: 'presence' | 'make_model';
  subject: string;
  /** The instance's name, as the equipment list names it ("Port engine"). */
  label: string;
  before: 'not_on_boat' | 'real' | null;
  after: 'not_on_boat' | 'real' | null;
  /** A `make_model` record's make and model now; null when Neo forgot them. */
  makeModel?: { make: string; model: string } | null;
  version: number | null;
  turnRef: string | null;
}

function presenceOf(v: unknown): 'not_on_boat' | 'real' | null {
  return v === 'not_on_boat' || v === 'real' ? v : null;
}

function makeModelOf(v: unknown): { make: string; model: string } | null {
  if (!isRecord(v)) return null;
  const make = stringOf(v.make);
  const model = stringOf(v.model);
  return make === undefined || model === undefined ? null : { make, model };
}

/** Decode a `data_answers` event (`{ changes }`) or the blocking body's `dataAnswers`. */
export function parseDataAnswerChanges(payload: unknown): NeoDataAnswerChange[] {
  const list = isRecord(payload) ? payload.changes : payload;
  const out: NeoDataAnswerChange[] = [];
  for (const raw of Array.isArray(list) ? list : []) {
    if (!isRecord(raw)) continue;
    const boatId = stringOf(raw.boatId);
    const subject = stringOf(raw.subject);
    if (boatId === undefined || subject === undefined) continue;
    if (raw.kind === 'make_model') {
      out.push({
        boatId,
        kind: 'make_model',
        subject,
        label: stringOf(raw.label) ?? subject,
        before: null,
        after: null,
        makeModel: makeModelOf(raw.after),
        version: versionOf(raw.version),
        turnRef: stringOf(raw.turnRef) ?? null,
      });
      continue;
    }
    out.push({
      boatId,
      subject,
      label: stringOf(raw.label) ?? subject,
      before: presenceOf(raw.before),
      after: presenceOf(raw.after),
      version: versionOf(raw.version),
      turnRef: stringOf(raw.turnRef) ?? null,
    });
  }
  return out;
}

/**
 * A note Neo changed in the boat's notebook (cloud ADR 0374, TOM-611): the
 * `notebook` record. The notebook is shared by every owner of the boat.
 */
export interface NeoNotebookRecord {
  boatId: string;
  /** The boat's name, for "Neo noted for <boat>". */
  boatName: string;
  action: string;
  noteId: string;
  /** The note's words after the change; null for a forget. */
  text: string | null;
  version: number | null;
  turnRef: string | null;
  /**
   * On a conversation read back: whether the turn's undo step is still on the
   * note. Null on the live stream, which has just made it.
   */
  undoable: boolean | null;
}

/** Decode a `notebook` event (`{ changes }`) or the blocking body's `notebook`. */
export function parseNotebookChanges(payload: unknown): NeoNotebookRecord[] {
  const list = isRecord(payload) ? payload.changes : payload;
  const out: NeoNotebookRecord[] = [];
  for (const raw of Array.isArray(list) ? list : []) {
    if (!isRecord(raw)) continue;
    const boatId = stringOf(raw.boatId);
    const noteId = stringOf(raw.noteId);
    const action = stringOf(raw.action);
    if (boatId === undefined || noteId === undefined || action === undefined) continue;
    out.push({
      boatId,
      boatName: stringOf(raw.boatName) ?? 'the boat',
      action,
      noteId,
      text: stringOf(raw.text) ?? null,
      version: versionOf(raw.version),
      turnRef: stringOf(raw.turnRef) ?? null,
      undoable: typeof raw.undoable === 'boolean' ? raw.undoable : null,
    });
  }
  return out;
}

/** What a line says happened, which picks its icon. */
export type ReceiptLineKind = 'added' | 'changed' | 'removed' | 'moved' | 'resized' | 'plain';

export interface ReceiptLine {
  key: string;
  kind: ReceiptLineKind;
  /** What the line shows. */
  text: string;
  /** What a screen reader reads: the verb with the line. */
  spoken: string;
}

export interface BoardReceiptPart {
  kind: 'board';
  key: string;
  boatId: string;
  slug: string;
  /** The board's title after the request. */
  boardTitle: string;
  heading: string;
  lines: ReceiptLine[];
  /** The request made this board, so its Undo removes it. */
  made: boolean;
  /** Everything the request named was already there: the part says so in place of its scope. */
  nothingChanged: boolean;
  /** The undo route's `expectedVersion`; null when the part offers no Undo. */
  undoVersion: number | null;
  /**
   * True when Undo puts back everything the part lists. Before TOM-346 a
   * board's undo step held only Neo's last write, so a request that wrote a
   * board more than once is undone one write back, and the receipt says so.
   */
  undoWhole: boolean;
  /** The board is still there after the request; Open board needs it. */
  exists: boolean;
  /** Items the request put on the board: off it again once undone. */
  addedItemIds: string[];
  /** The request that wrote the board, when its writes name one: the whole-request Undo's (TOM-595). */
  turnRef: string | null;
}

/** The answers about one boat's equipment one request wrote (TOM-409). */
export interface EquipmentReceiptPart {
  kind: 'equipment';
  key: string;
  boatId: string;
  heading: string;
  lines: ReceiptLine[];
  /** The request's turn: Undo names it (POST /v1/boats/:boatId/equipment/undo). */
  turnRef: string | null;
  /**
   * Each answer the request wrote, at its version after the request, which the
   * step check compares; `kind` is absent for a presence answer. `cleared`: a
   * make and model the request forgot (TOM-878).
   */
  answers: {
    subject: string;
    version: number | null;
    kind?: 'presence' | 'make_model';
    cleared?: true;
  }[];
  /** Non-null while the part offers Undo, as the other parts' versions do. */
  undoVersion: number | null;
}

export interface LabelReceiptPart {
  kind: 'label';
  key: string;
  boatId: string;
  requestId: string;
  target: BoatNameTarget;
  heading: string;
  lines: ReceiptLine[];
  undoVersion: number;
  turnRef: string;
}

/**
 * The notes one request changed in one boat's notebook (TOM-611): "Neo noted
 * for <boat>", shared with everyone on the boat. Its Undo is the whole
 * request's (POST /v1/neo/turns/:turnRef/undo); a forget is final and offers
 * none.
 */
export interface NotebookReceiptPart {
  kind: 'notebook';
  key: string;
  boatId: string;
  boatName: string;
  heading: string;
  lines: ReceiptLine[];
  /** The request's turn: its whole-request Undo names it. */
  turnRef: string | null;
  /** Non-null while the part offers Undo, as the other parts' versions do. */
  undoVersion: number | null;
  /**
   * Read back, none of its notes carries the request's undo step any more:
   * undone already, or changed since. The receipt says so instead of Undo.
   */
  stepGone: boolean;
}

export type ReceiptPart =
  | BoardReceiptPart
  | EquipmentReceiptPart
  | LabelReceiptPart
  | NotebookReceiptPart;

const SIZE_WORD: Readonly<Record<NeoBoardItemSize, string>> = {
  s: 'small',
  m: 'medium',
  l: 'large',
};

/** Actions that wrote the board; `already_pinned` wrote nothing and `undone` is Neo's own undo. */
const WRITES: ReadonlySet<NeoBoardChange['action']> = new Set([
  'created',
  'pinned',
  'unpinned',
  'changed',
  'moved',
  'resized',
  'renamed',
]);

function itemLine(
  key: string,
  kind: ReceiptLineKind,
  title: string,
  size: NeoBoardItemSize | null
): ReceiptLine {
  switch (kind) {
    case 'added': {
      // A size is named only when it is not the usual medium.
      const shown = size !== null && size !== 'm' ? `${title} · ${SIZE_WORD[size]}` : title;
      return { key, kind, text: shown, spoken: `Added ${shown}` };
    }
    case 'changed':
      return { key, kind, text: title, spoken: `Changed ${title}` };
    case 'removed':
      return { key, kind, text: `${title}, removed`, spoken: `Removed ${title}` };
    case 'moved':
      return { key, kind, text: `${title}, moved`, spoken: `Moved ${title}` };
    case 'resized': {
      const word = size === null ? 'resized' : SIZE_WORD[size];
      return { key, kind, text: `${title}, ${word}`, spoken: `Resized ${title}, ${word}` };
    }
    case 'plain':
      return { key, kind, text: title, spoken: title };
  }
}

function boardPart(records: readonly NeoBoardChange[]): BoardReceiptPart {
  const last = records[records.length - 1]!;
  const boardTitle = last.boardTitle;
  const made = records.some((r) => r.action === 'created');
  const writes = records.filter((r) => WRITES.has(r.action));

  // One line per item, in the order the request first touched it. An item the
  // request added stays "added" through later changes to it in the request.
  const lines = new Map<string, ReceiptLine>();
  const added: string[] = [];
  const kept: string[] = [];
  let renamed: string | null = null;
  for (const r of records) {
    if (r.action === 'created') {
      for (const item of r.items ?? []) {
        lines.set(item.itemId, itemLine(item.itemId, 'added', item.title, item.size));
        added.push(item.itemId);
      }
      continue;
    }
    if (r.action === 'renamed') {
      renamed = r.title;
      continue;
    }
    if (r.action === 'undone' || r.itemId === null) continue;
    const id = r.itemId;
    const wasAdded = added.includes(id);
    switch (r.action) {
      case 'pinned':
        if (!wasAdded) added.push(id);
        lines.set(id, itemLine(id, 'added', r.title, r.size));
        break;
      case 'already_pinned':
        if (!lines.has(id)) kept.push(r.title);
        break;
      case 'unpinned':
        if (wasAdded) {
          // Put on and taken off in one request: nothing to show or undo.
          lines.delete(id);
          added.splice(added.indexOf(id), 1);
        } else {
          lines.set(id, itemLine(id, 'removed', r.title, r.size));
        }
        break;
      case 'changed':
        lines.set(id, itemLine(id, wasAdded ? 'added' : 'changed', r.title, r.size));
        break;
      case 'resized':
        lines.set(id, itemLine(id, wasAdded ? 'added' : 'resized', r.title, r.size));
        break;
      case 'moved':
        if (!lines.has(id)) lines.set(id, itemLine(id, 'moved', r.title, r.size));
        break;
    }
  }

  let heading: string;
  let nothingChanged = false;
  const out = [...lines.values()];
  if (made) {
    heading = `Made ${boardTitle}`;
  } else if (writes.length === 0 && records.some((r) => r.action === 'undone')) {
    heading = `Undid Neo’s change to ${boardTitle}`;
  } else if (writes.length === 0) {
    // Only items that were already there: nothing changed.
    nothingChanged = true;
    if (kept.length === 1) {
      heading = `${kept[0]} was already on ${boardTitle}`;
    } else {
      heading = `Already on ${boardTitle}`;
      kept.forEach((title, i) => out.push(itemLine(`kept-${i}`, 'plain', title, null)));
    }
  } else if (out.length === 0 && renamed !== null) {
    heading = `Renamed the board to ${boardTitle}`;
  } else {
    heading = `Changed ${boardTitle}`;
    if (renamed !== null) out.unshift(itemLine('renamed', 'plain', `Renamed to ${renamed}`, null));
    kept.forEach((title, i) =>
      out.push(itemLine(`kept-${i}`, 'plain', `${title}, already on the board`, null))
    );
  }

  // The undo names the version of the request's last write, while no undo of
  // Neo's own came after it.
  let undoVersion: number | null = null;
  for (const r of records) {
    if (r.action === 'undone') undoVersion = null;
    else if (WRITES.has(r.action)) undoVersion = r.version;
  }
  const turnRefs = new Set(writes.map((r) => r.turnRef));
  const undoWhole = writes.length <= 1 || (turnRefs.size === 1 && !turnRefs.has(null));
  const turnRef = turnRefs.size === 1 ? ([...turnRefs][0] ?? null) : null;

  return {
    kind: 'board',
    key: `board:${last.boatId}:${last.slug}`,
    boatId: last.boatId,
    slug: last.slug,
    boardTitle,
    heading,
    lines: out,
    made,
    nothingChanged,
    undoVersion,
    undoWhole,
    exists: last.version !== null,
    addedItemIds: added,
    turnRef,
  };
}

const ANSWER_WORDS: Readonly<Record<'not_on_boat' | 'real', string>> = {
  not_on_boat: 'not on this boat',
  real: 'on this boat',
};

function equipmentPart(records: readonly NeoDataAnswerChange[]): EquipmentReceiptPart {
  // The last answer about each instance, in the order the request first named it.
  const last = new Map<string, NeoDataAnswerChange>();
  for (const r of records) last.set(`${r.kind ?? 'presence'} ${r.subject}`, r);
  const lines: ReceiptLine[] = [...last.values()].map((r) => {
    if (r.kind === 'make_model') {
      const key = `make_model:${r.subject}`;
      if (r.makeModel == null) {
        return {
          key,
          kind: 'changed',
          text: `${r.label}: make and model forgotten`,
          spoken: `${r.label}: make and model forgotten`,
        };
      }
      const words = `${r.makeModel.make} ${r.makeModel.model}`;
      return {
        key,
        kind: 'changed',
        text: `${r.label}: ${words}`,
        spoken: `${r.label} saved as ${words}`,
      };
    }
    if (r.after === null) {
      return {
        key: r.subject,
        kind: 'changed',
        text: `${r.label}: Neomaris decides`,
        spoken: `${r.label}: answer cleared, Neomaris decides`,
      };
    }
    const words = ANSWER_WORDS[r.after];
    return {
      key: r.subject,
      kind: r.after === 'not_on_boat' ? 'removed' : 'added',
      text: `${r.label}: ${words}`,
      spoken: `${r.label} marked ${words}`,
    };
  });
  const first = records[0]!;
  const turnRef = records.find((r) => r.turnRef !== null)?.turnRef ?? null;
  return {
    kind: 'equipment',
    key: `equipment:${first.boatId}`,
    boatId: first.boatId,
    heading: 'Updated what’s aboard',
    lines,
    turnRef,
    answers: [...last.values()].map((r) => ({
      subject: r.subject,
      version: r.version,
      ...(r.kind === 'make_model'
        ? {
            kind: 'make_model' as const,
            ...(r.makeModel == null ? { cleared: true as const } : {}),
          }
        : {}),
    })),
    undoVersion: turnRef === null ? null : (records[records.length - 1]!.version ?? 0),
  };
}

/** What a notebook line says each change did, and the icon it takes. */
function notebookLine(r: NeoNotebookRecord): ReceiptLine {
  const text = r.text ?? '';
  switch (r.action) {
    case 'forgotten':
      return { key: r.noteId, kind: 'removed', text: 'Forgot a note', spoken: 'Forgot a note' };
    case 'confirmed':
      return { key: r.noteId, kind: 'changed', text, spoken: `Confirmed: ${text}` };
    case 'corrected':
      return { key: r.noteId, kind: 'changed', text, spoken: `Corrected: ${text}` };
    case 'contradicted':
      return {
        key: r.noteId,
        kind: 'removed',
        text: `${text} (out of date)`,
        spoken: `Marked out of date: ${text}`,
      };
    case 'disputed':
      return {
        key: r.noteId,
        kind: 'plain',
        text: `${text} (the readings disagree)`,
        spoken: `The readings disagree with: ${text}`,
      };
    case 'set_aside':
      return {
        key: r.noteId,
        kind: 'removed',
        text: `${text} (set aside to make room)`,
        spoken: `Set aside to make room: ${text}`,
      };
    case 'reaffirmed':
      return { key: r.noteId, kind: 'plain', text, spoken: `Seen again in the readings: ${text}` };
    default:
      return { key: r.noteId, kind: 'added', text, spoken: `Noted: ${text}` };
  }
}

function notebookPart(records: readonly NeoNotebookRecord[]): NotebookReceiptPart {
  // The last change to each note, in the order the request first changed it.
  const last = new Map<string, NeoNotebookRecord>();
  for (const r of records) last.set(r.noteId, r);
  const changes = [...last.values()];
  const first = records[0]!;
  const noted = changes.some((r) =>
    ['added', 'replaced', 'corrected', 'confirmed'].includes(r.action)
  );
  // A forget is final: a part that only forgot offers no Undo.
  const undoable = changes.filter((r) => r.action !== 'forgotten');
  const turnRef = undoable.find((r) => r.turnRef !== null)?.turnRef ?? null;
  return {
    kind: 'notebook',
    // Each request keeps its own undo step on each note (cloud ADR 0370 §6), so
    // a later request's part never supersedes this one: its key names the turn.
    key: `notebook:${first.boatId}:${turnRef ?? first.noteId}`,
    boatId: first.boatId,
    boatName: first.boatName,
    heading: noted ? `Neo noted for ${first.boatName}` : `Neo changed ${first.boatName}’s notebook`,
    lines: changes.map(notebookLine),
    turnRef,
    undoVersion: turnRef === null || undoable.length === 0 ? null : (undoable.at(-1)!.version ?? 0),
    stepGone: undoable.length > 0 && undoable.every((r) => r.undoable === false),
  };
}

/**
 * One request's receipt: a part per board it changed, in order, then the
 * equipment answers per boat, then the names, then the boat's notebook.
 */
export function buildReceipt(
  boards: readonly NeoBoardChange[],
  answers: readonly NeoDataAnswerChange[] = [],
  labels: readonly NeoLabelChange[] = [],
  notebook: readonly NeoNotebookRecord[] = []
): ReceiptPart[] {
  const byBoard = new Map<string, NeoBoardChange[]>();
  for (const change of boards) {
    const key = `${change.boatId}\u0000${change.slug}`;
    const list = byBoard.get(key);
    if (list === undefined) byBoard.set(key, [change]);
    else list.push(change);
  }
  const parts: ReceiptPart[] = [...byBoard.values()].map(boardPart);
  const answersByBoat = new Map<string, NeoDataAnswerChange[]>();
  for (const change of answers) {
    const list = answersByBoat.get(change.boatId);
    if (list === undefined) answersByBoat.set(change.boatId, [change]);
    else list.push(change);
  }
  for (const list of answersByBoat.values()) parts.push(equipmentPart(list));
  const byTarget = new Map<string, NeoLabelChange>();
  for (const change of labels)
    byTarget.set(`label:${change.boatId}:${boatNameTargetKey(change.target)}`, change);
  for (const [key, change] of byTarget) {
    const text = `${change.beforeLabel} → ${change.label}`;
    parts.push({
      kind: 'label',
      key,
      boatId: change.boatId,
      requestId: change.requestId,
      target: change.target,
      heading: 'Renamed everywhere',
      lines: [
        { key, kind: 'changed', text, spoken: `Renamed ${change.beforeLabel} to ${change.label}` },
      ],
      undoVersion: change.version,
      turnRef: change.turnRef,
    });
  }
  const notesByBoat = new Map<string, NeoNotebookRecord[]>();
  for (const change of notebook) {
    const list = notesByBoat.get(change.boatId);
    if (list === undefined) notesByBoat.set(change.boatId, [change]);
    else list.push(change);
  }
  for (const list of notesByBoat.values()) parts.push(notebookPart(list));
  return parts;
}

/**
 * Which parts of each receipt a later request has superseded. A board keeps
 * one undo step, the last request's (cloud ADR 0302 §3), so an earlier
 * receipt's part for the same board
 * offers no Undo. A later part that changed nothing (`already_pinned` only)
 * supersedes nothing. Takes the receipts in conversation order; returns each
 * one's superseded part keys.
 */
export function supersededParts(receipts: readonly (readonly ReceiptPart[])[]): Set<string>[] {
  const later = new Set<string>();
  const out = receipts.map(() => new Set<string>());
  for (let i = receipts.length - 1; i >= 0; i--) {
    const parts = receipts[i]!;
    for (const part of parts) if (later.has(part.key)) out[i]!.add(part.key);
    for (const part of parts) {
      if (part.kind === 'board' && part.nothingChanged) continue;
      later.add(part.key);
    }
  }
  return out;
}

/** What a superseded part says in place of Undo (boards-v3.md, chat record "Stale"). */
export function receiptSupersededText(part: ReceiptPart): string {
  if (part.kind === 'board')
    return `${part.boardTitle} changed again since, so Undo isn’t offered.`;
  if (part.kind === 'equipment') return 'What’s aboard changed again since, so Undo isn’t offered.';
  if (part.kind === 'notebook') return 'This note changed again since, so Undo isn’t offered.';
  return 'This name changed again since, so Undo isn’t offered.';
}

/** True when the receipt offers Undo: some part has a change to undo. */
export function receiptCanUndo(parts: readonly ReceiptPart[]): boolean {
  return parts.some((p) => p.undoVersion !== null);
}

/** The line under a part's heading: its scope (boards-v3.md "Words"), or that nothing changed. */
export function receiptScope(part: ReceiptPart, boatName: string | null): string {
  if (part.kind === 'label') return `Every owner · everywhere on ${boatName ?? 'this boat'}`;
  if (part.kind === 'equipment')
    return `Every owner · boards, pickers and Neo · ${boatName ?? 'this boat'}`;
  // The notebook is the boat's (TOM-611): the receipt says who shares it.
  if (part.kind === 'notebook') return 'Shared with everyone on the boat';
  if (part.nothingChanged) return 'Nothing changed';
  return `Shared with ${boatName ?? 'the boat'}’s owners`;
}

/**
 * What one part's undo came to: done (and whether the board went), refused,
 * or failed for another reason, when nothing is known to have changed. A
 * refusal says why: the row `changed` since (`409 undo_stale`), there is
 * `nothing` of Neo's left to undo (`404 nothing_to_undo`: undone already, or
 * cleared by an owner's change), or the board is `gone`.
 */
export type ReceiptUndoOutcome =
  /**
   * `removed`: the undo removed a board Neo had made. `kept`: an equipment
   * part's answers an owner changed since, left as they are.
   */
  | { kind: 'undone'; removed: boolean; kept?: number }
  | { kind: 'refused'; reason: 'changed' | 'nothing' | 'gone' }
  | { kind: 'failed' };

function statusOf(err: unknown): number | undefined {
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Undo one part. The key is the part's own, kept across Try again, so a retry
 * of an undo whose answer was lost replays it rather than refusing it.
 */
export async function undoReceiptPart(
  part: ReceiptPart,
  idempotencyKey: string
): Promise<ReceiptUndoOutcome> {
  if (part.undoVersion === null) return { kind: 'refused', reason: 'nothing' };
  if (part.kind === 'equipment') return undoEquipmentPart(part, idempotencyKey);
  // A note has no route of its own: the whole request's undo puts it back (TOM-611).
  if (part.kind === 'notebook') {
    if (part.turnRef === null) return { kind: 'refused', reason: 'nothing' };
    return receiptTurnOutcome(part, await undoTurn(part.turnRef, idempotencyKey));
  }
  if (part.kind === 'label') {
    try {
      const res = await api<unknown>(`/v1/neo/label-changes/${part.requestId}/undo`, {
        method: 'POST',
        idempotencyKey,
      });
      announceUndoChatNote(res);
      return { kind: 'undone', removed: false };
    } catch (error) {
      if (statusOf(error) === 409) return { kind: 'refused', reason: 'changed' };
      if (statusOf(error) === 404) return { kind: 'refused', reason: 'nothing' };
      return { kind: 'failed' };
    }
  }
  const path = withBoardKinds(
    `/v1/boats/${part.boatId}/boards/${encodeURIComponent(part.slug)}/undo`
  );
  try {
    const res = await api<{ board?: unknown }>(path, {
      method: 'POST',
      body: { expectedVersion: part.undoVersion },
      idempotencyKey,
    });
    announceUndoChatNote(res);
    return { kind: 'undone', removed: isRecord(res) && res.board === null };
  } catch (err) {
    const status = statusOf(err);
    // Nothing was written for this part either way.
    if (status === 409) return { kind: 'refused', reason: 'changed' };
    if (status === 404) {
      const body = (err as { body?: unknown }).body;
      const code = isRecord(body) ? body.error : undefined;
      return { kind: 'refused', reason: code === 'nothing_to_undo' ? 'nothing' : 'gone' };
    }
    return { kind: 'failed' };
  }
}

/**
 * Undo one request's answers on one boat (cloud TOM-408): each by
 * compare-and-swap on its own step. Refused when none was undone: `changed`
 * when an owner answered since, `nothing` when they were undone already.
 */
async function undoEquipmentPart(
  part: EquipmentReceiptPart,
  idempotencyKey: string
): Promise<ReceiptUndoOutcome> {
  if (part.turnRef === null) return { kind: 'refused', reason: 'nothing' };
  try {
    const res = await api<{ undone?: unknown; notUndone?: unknown }>(
      `/v1/boats/${part.boatId}/equipment/undo`,
      { method: 'POST', body: { turnRef: part.turnRef }, idempotencyKey }
    );
    announceUndoChatNote(res);
    const undone = isRecord(res) && Array.isArray(res.undone) ? res.undone.length : 0;
    const notUndone = isRecord(res) && Array.isArray(res.notUndone) ? res.notUndone : [];
    const kept = notUndone.filter((p) => isRecord(p) && p.reason === 'changed_since').length;
    if (undone > 0)
      return kept > 0
        ? { kind: 'undone', removed: false, kept }
        : { kind: 'undone', removed: false };
    return { kind: 'refused', reason: kept > 0 ? 'changed' : 'nothing' };
  } catch (err) {
    const status = statusOf(err);
    if (status === 404) return { kind: 'refused', reason: 'nothing' };
    return { kind: 'failed' };
  }
}

/** What the receipt says about one part once its undo has an answer (boards-v3.md). */
export function receiptOutcomeText(part: ReceiptPart, outcome: ReceiptUndoOutcome): string {
  if (outcome.kind === 'failed') {
    return 'Couldn’t undo. Nothing changed. Check the connection and try again.';
  }
  if (part.kind === 'notebook') {
    if (outcome.kind === 'refused') {
      return outcome.reason === 'changed'
        ? 'An owner changed this note since, so it can’t be undone here.'
        : 'There’s nothing left to undo here. It was undone, or changed since.';
    }
    if (outcome.kind === 'undone' && (outcome.kept ?? 0) > 0) {
      return 'Undone, apart from a note an owner changed since.';
    }
    return `Undone. ${part.boatName}’s notebook is back as it was before this request.`;
  }
  if (part.kind === 'label') {
    if (outcome.kind === 'refused')
      return outcome.reason === 'changed'
        ? 'An owner changed this name since, so it can’t be undone here.'
        : 'There’s nothing left to undo for this name.';
    return 'Undone. The previous name is restored everywhere.';
  }
  if (part.kind === 'equipment') {
    if (outcome.kind === 'refused') {
      return outcome.reason === 'changed'
        ? 'An owner answered about this since, so it can’t be undone here. The Equipment page has it.'
        : 'There’s nothing left to undo here. It was undone, or changed since.';
    }
    if (outcome.kind === 'undone' && (outcome.kept ?? 0) > 0) {
      const n = outcome.kept!;
      return `Undone, apart from ${n === 1 ? 'one answer' : `${n} answers`} an owner changed since. The Equipment page has ${n === 1 ? 'it' : 'them'}.`;
    }
    return 'Undone. What’s aboard is back as it was before this request.';
  }
  const title = part.boardTitle;
  if (outcome.kind === 'refused') {
    if (outcome.reason === 'gone') return `${title} isn’t there any more.`;
    if (outcome.reason === 'nothing') {
      return `There’s nothing left to undo on ${title}. It was undone, or changed since.`;
    }
    return part.made
      ? `${title} changed since, so it can’t be removed with Undo.`
      : `${title} changed since, so this part can’t be undone.`;
  }
  if (outcome.removed) return `Undone. ${title} is removed.`;
  if (!part.undoWhole) {
    return `Undid Neo’s last change to ${title}. Its earlier changes in this request stay.`;
  }
  return `Undone. ${title} is back as it was before this request.`;
}

/**
 * The one request every part with an Undo names, or null: no part has an
 * Undo, a part names no request, or they name more than one. A request that
 * also changed the owner's own looks undoes as a whole by it (TOM-595).
 */
export function receiptTurnRef(parts: readonly ReceiptPart[]): string | null {
  const refs = new Set(parts.filter((p) => p.undoVersion !== null).map((p) => p.turnRef));
  return refs.size === 1 ? ([...refs][0] ?? null) : null;
}

/**
 * The requests whose receipt has a note to undo (TOM-611): a note has no route
 * of its own, so the receipt's one Undo is the whole request's.
 */
export function notebookTurnRefs(receipts: readonly (readonly ReceiptPart[])[]): Set<string> {
  const out = new Set<string>();
  for (const parts of receipts) {
    if (!parts.some((p) => p.kind === 'notebook' && p.undoVersion !== null)) continue;
    const ref = receiptTurnRef(parts);
    if (ref !== null) out.add(ref);
  }
  return out;
}

/**
 * One part of what the whole-request undo did (`POST /v1/neo/turns/:turnRef/undo`,
 * cloud ADR 0307 §6), as a receipt's part matches it: `key` is the board's
 * slug, the answer's subject, the name's target key or the note's id
 * (TOM-611), and empty for the owner's looks.
 */
export interface TurnUndoEntry {
  part: 'board' | 'data_answer' | 'label' | 'looks' | 'notebook';
  boatId: string;
  key: string;
  /** An undone board's version after the undo: null when the undo removed it. */
  version?: number | null;
  /**
   * Why a part was left as it is. A note also has `no_room` (TOM-611): the
   * notebook had no room for it to come back into use.
   */
  reason?: NeoTurnUndoReason | 'no_room';
}

/**
 * What the whole-request undo came to: which parts it undid and which it
 * left, or failed, when nothing is known to have changed. A request the
 * server doesn't know as the owner's (404) has nothing left to undo.
 */
export type TurnUndoAnswer =
  | { kind: 'answered'; undone: TurnUndoEntry[]; notUndone: TurnUndoEntry[] }
  | { kind: 'failed' };

const TURN_UNDO_PARTS: ReadonlySet<string> = new Set([
  'board',
  'data_answer',
  'label',
  'looks',
  'notebook',
]);
const TURN_UNDO_REASONS: ReadonlySet<string> = new Set([
  'changed_since',
  'removed',
  'nothing_to_undo',
  'no_room',
]);

function turnUndoEntries(list: unknown): TurnUndoEntry[] {
  const out: TurnUndoEntry[] = [];
  for (const raw of Array.isArray(list) ? list : []) {
    if (!isRecord(raw) || typeof raw.part !== 'string' || !TURN_UNDO_PARTS.has(raw.part)) continue;
    const boatId = stringOf(raw.boatId);
    if (boatId === undefined) continue;
    let key = '';
    if (raw.part === 'board') key = stringOf(raw.slug) ?? '';
    else if (raw.part === 'data_answer') key = stringOf(raw.subject) ?? '';
    else if (raw.part === 'notebook') key = stringOf(raw.noteId) ?? '';
    else if (raw.part === 'label') {
      const target = parseBoatNameTarget(raw.target);
      if (target === null) continue;
      key = boatNameTargetKey(target);
    }
    const entry: TurnUndoEntry = { part: raw.part as TurnUndoEntry['part'], boatId, key };
    if (raw.part === 'board') entry.version = versionOf(raw.version);
    if (typeof raw.reason === 'string' && TURN_UNDO_REASONS.has(raw.reason)) {
      entry.reason = raw.reason as TurnUndoEntry['reason'];
    }
    out.push(entry);
  }
  return out;
}

/** Read the whole-request undo's 200 body (`{ undone, notUndone }`). */
export function parseTurnUndo(body: unknown): TurnUndoAnswer {
  return {
    kind: 'answered',
    undone: turnUndoEntries(isRecord(body) ? body.undone : undefined),
    notUndone: turnUndoEntries(isRecord(body) ? body.notUndone : undefined),
  };
}

/**
 * Undo one request as a whole: its boards, answers, names and the owner's own
 * looks, in one transaction on the server (TOM-595). The key is the request's
 * own, kept across Try again, so a retry replays an undo whose answer was lost.
 */
export async function undoTurn(turnRef: string, idempotencyKey: string): Promise<TurnUndoAnswer> {
  try {
    const res = await api<unknown>(`/v1/neo/turns/${encodeURIComponent(turnRef)}/undo`, {
      method: 'POST',
      idempotencyKey,
    });
    announceUndoChatNote(res);
    return parseTurnUndo(res);
  } catch (err) {
    if (statusOf(err) === 404) return { kind: 'answered', undone: [], notUndone: [] };
    return { kind: 'failed' };
  }
}

function refusalOf(reason: TurnUndoEntry['reason']): 'changed' | 'nothing' | 'gone' {
  if (reason === 'changed_since' || reason === 'no_room') return 'changed';
  if (reason === 'removed') return 'gone';
  return 'nothing';
}

/** What the whole-request undo came to for one part of the shared receipt. */
export function receiptTurnOutcome(part: ReceiptPart, answer: TurnUndoAnswer): ReceiptUndoOutcome {
  if (answer.kind === 'failed') return { kind: 'failed' };
  if (part.kind === 'notebook') {
    // Every note the request changed on this boat (TOM-611).
    const ours = (e: TurnUndoEntry) => e.part === 'notebook' && e.boatId === part.boatId;
    const undone = answer.undone.filter(ours).length;
    const kept = answer.notUndone.filter(
      (e) => ours(e) && refusalOf(e.reason) === 'changed'
    ).length;
    if (undone > 0)
      return kept > 0
        ? { kind: 'undone', removed: false, kept }
        : { kind: 'undone', removed: false };
    return { kind: 'refused', reason: kept > 0 ? 'changed' : 'nothing' };
  }
  if (part.kind === 'equipment') {
    const ours = (e: TurnUndoEntry) => e.part === 'data_answer' && e.boatId === part.boatId;
    const undone = answer.undone.filter(ours).length;
    const kept = answer.notUndone.filter((e) => ours(e) && e.reason === 'changed_since').length;
    if (undone > 0)
      return kept > 0
        ? { kind: 'undone', removed: false, kept }
        : { kind: 'undone', removed: false };
    return { kind: 'refused', reason: kept > 0 ? 'changed' : 'nothing' };
  }
  const ours =
    part.kind === 'board'
      ? (e: TurnUndoEntry) => e.part === 'board' && e.boatId === part.boatId && e.key === part.slug
      : (e: TurnUndoEntry) =>
          e.part === 'label' &&
          e.boatId === part.boatId &&
          e.key === boatNameTargetKey(part.target);
  const done = answer.undone.find(ours);
  if (done !== undefined) return { kind: 'undone', removed: done.version === null };
  const reason = refusalOf(answer.notUndone.find(ours)?.reason);
  // A name has no board to be gone.
  return {
    kind: 'refused',
    reason: part.kind === 'label' && reason === 'gone' ? 'nothing' : reason,
  };
}

/** The board page a part opens. */
export function receiptBoardHref(part: BoardReceiptPart): string {
  return `/dashboard/boats/${part.boatId}/boards/${encodeURIComponent(part.slug)}`;
}
